import "../k";
import { LEVELS } from "../data";
import { addButton } from "../things/button";
import { goTransition } from "../util/goTransition";
import { clearBestScores, getBestScore } from "../util/storage";
import { QUIZ_GREEN, QUIZ_PINK } from "../util/style";

type LevelState = "unlocked" | "locked" | "comingSoon";

export function getLevelStateInfo(levelIndex: number): {
  state: LevelState;
  prevLevelUnlocked: boolean;
  prevLevelName?: string;
  prevLevelUnlockScore?: string;
} {
  if (levelIndex >= LEVELS.length) {
    return { state: "comingSoon", prevLevelUnlocked: false };
  }

  if (levelIndex === 0) {
    return { state: "unlocked", prevLevelUnlocked: true };
  }

  const prevLevelIndex = levelIndex - 1;
  const prevLevel = LEVELS[prevLevelIndex];
  const prevBestScore = getBestScore(prevLevel.fileName);
  const prevMaxScore = prevLevel.questions.length;

  const prevLevelUnlocked =
    prevBestScore !== null && prevBestScore > prevMaxScore / 2;

  if (prevLevelUnlocked) {
    return { state: "unlocked", prevLevelUnlocked: true };
  } else {
    const requiredScore = Math.ceil(prevMaxScore / 2) + 1;
    return {
      state: "locked",
      prevLevelUnlocked: false,
      prevLevelName: prevLevel.name,
      prevLevelUnlockScore: `${requiredScore}/${prevMaxScore}`,
    };
  }
}

scene("levelSelect", () => {
  onKeyPress("0", () => {
    clearBestScores();
  });

  const backButton = addButton("back", [pos(100, 100), "backButton"]);
  backButton.onClick(() => {
    goTransition("start");
  });

  add([
    text("Level Select", { size: 96 / 2, font: "rumeur" }),
    pos(width() / 2, height() * 0.2),
    anchor("center"),
    color(Color.BLACK),
  ]);

  const numCols = 4;
  const levelWidth = 300;
  const levelHeight = 140;
  const spacing = 5;
  const totalSlots = 16;

  const totalGridWidth = numCols * levelWidth + (numCols - 1) * spacing;
  const startX = (width() - totalGridWidth) / 2;
  const startY = height() * 0.2 + 140;

  for (let i = 0; i < LEVELS.length; i++) { // Iterate through LEVELS.length instead of totalSlots
    const level = LEVELS[i];
    const col = i % numCols;
    const row = Math.floor(i / numCols);

    const xPos = startX + col * (levelWidth + spacing) + levelWidth / 2;
    const yPos = startY + row * (levelHeight + spacing);

    const bestScore = getBestScore(level.fileName);
    const levelStateInfo = getLevelStateInfo(i); // Still pass index for now, will adjust getLevelStateInfo signature later

    let buttonColor, textColor;
    let clickHandler: (() => void) | undefined;
    let isEnabled: boolean;
    let mainText: string;

    switch (levelStateInfo.state) {
      case "unlocked":
        buttonColor = bestScore !== null ? QUIZ_GREEN : Color.WHITE;
        textColor = Color.BLACK;
        clickHandler = () => goTransition("levelLoading", i, level.fileName);
        isEnabled = true;
        mainText = level.name;
        break;
      case "locked":
        buttonColor = QUIZ_PINK; // Grey for locked
        textColor = Color.WHITE; // Dark grey for locked text
        clickHandler = undefined;
        isEnabled = false;
        mainText = level.name; // Still show level name even if locked
        break;
      case "comingSoon":
        buttonColor = new Color(127, 127, 127);
        textColor = new Color(80, 80, 80);
        clickHandler = undefined;
        isEnabled = false;
        mainText = "Coming soon...";
        break;
    }

    const levelButton = addButton(
      "button",
      [pos(xPos, yPos), color(buttonColor), { textContents: mainText }],
      vec2(levelWidth, levelHeight)
    );
    levelButton.isEnabled = isEnabled;

    // Add additional text based on state
    if (levelStateInfo.state === "unlocked" && bestScore !== null) {
      levelButton.add([
        text(`BEST: ${bestScore}/${level.questions.length}`, {
          size: 16,
          font: "rumeur",
        }),
        anchor("center"),
        color(textColor),
        pos(0, 35),
      ]);
    } else if (levelStateInfo.state === "locked") {
      const prevLevel = LEVELS[i - 1];
      const aboutToUnlock = (
        i > 0 &&
        getBestScore(prevLevel.fileName) !== null &&
        getBestScore(prevLevel.fileName)! > prevLevel.questions.length / 2
      );
      const textContent = aboutToUnlock ? `UNLOCK: ${levelStateInfo.prevLevelUnlockScore}!` : "LOCKED!";

      levelButton.add([
        text(textContent, { size: 20, font: "rumeur" }),
        anchor("center"),
        color(textColor),
        pos(0, 35),
      ]);
    }

    if (clickHandler) {
      levelButton.onClick(clickHandler);
    }
  }
});
